import { addMonths } from "date-fns";
import React, { forwardRef, useRef, useState, useImperativeHandle } from "react";
import { RCCGrid, RCCHookToast, RCCRadio, RCCRefModalProps, RCCRefSpreadsheetProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import LoadingContainer from "src/components/LoadingContainer";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import Spreadsheet from "src/components/Spreadsheet";
import Tabs from "src/components/Tabs";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import formatNumber from "src/utils/formatNumber";
import { ITabScreenBudgets } from "../ScreenBudgets/interface";
import { IModalPopulateValuesPlanned, IOpenModalPopulateValuesParams, IRefModalPopulateValuesPlanned } from "./interface";

const ModalPopulateValuesPlanned: React.ForwardRefRenderFunction<IRefModalPopulateValuesPlanned, IModalPopulateValuesPlanned> = ({
	origin,
	tabs,
	onClose
}, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
    
	// Modal Populate
	const modalPopulate = useRef<RCCRefModalProps>(null);
	const [subtitleModal, setSubtitleModal] = useState<string | undefined>(undefined);
	const [memorizedParams, setMemorizedParams] = useState<IOpenModalPopulateValuesParams>({period: String(new Date().getFullYear()) } as IOpenModalPopulateValuesParams);
	const buttonSubmitFormRef = useRef<HTMLButtonElement>(null);
	const [tabSelected, setTabSelected] = useState<ITabScreenBudgets>(tabs[0]);
	const spreadsheetRef = useRef<RCCRefSpreadsheetProps>(null);
	const spreadsheetValues = useRef<string[][]>([[]]);
	const onCallbackFunction = useRef(() => {});
	const [startDate, setStartDate] = useState(new Date());
	const [isLoading, setIsLoading] = useState(false);
	const openModalPopulateValuesPlanned = async ({ ofa001Id, subtitle, subsidiary, lov1, lov2, lov3, period, startMonth }: IOpenModalPopulateValuesParams, onCallback: () => void) => {
		setMemorizedParams({
			subsidiary,
			lov1,
			lov2,
			ofa001Id,
			period,
			subtitle,
			lov3,
			startMonth
		});
		const newDate = new Date(Number(period), startMonth - 1, 1);		
		onCallbackFunction.current = onCallback;
		setTabSelected(tabs[0]);
		setIsLoading(false);
		setStartDate(newDate);
		setSubtitleModal(subtitle);
		modalPopulate.current?.openModal();
	};
	const {register, handleSubmit} = useForm();
	const submitSpreadsheet = async (data: any) => {
		try{
			setIsLoading(true);
			await RCCServicesApi.postRequest({
				method: "FinancialManagement/PopulateValuesFinancialManagementSpreadsheet",
				body: {
					spreadsheet: {
						data: spreadsheetValues.current
					},
					hierarchy1: memorizedParams.lov1.value,
					hierarchy2: memorizedParams.lov2.value,
					hierarchy3: memorizedParams.lov3 ? memorizedParams.lov3.value : undefined,
					budgetPlanId: memorizedParams.ofa001Id,
					currentLanguage,
					subsidiary: memorizedParams.subsidiary.value,
					origin,
					type: tabSelected.value,
					period: memorizedParams.period,
					...data
				}
			});

			addToast({
				color: "green",
				description: translate("Valores adicionados com sucesso"),
				title: translate("Sucesso")
			});
			onCallbackFunction.current();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao adicionar valores"), addToast);
		}finally{
			setIsLoading(false);
		}
	};
	const onSaveSpreadsheet = () => {
		const newValuesMonthlyData: string[][] = spreadsheetRef.current ? spreadsheetRef.current.getData() as string[][] : [];
		newValuesMonthlyData.pop();
		newValuesMonthlyData.pop();
		spreadsheetValues.current = newValuesMonthlyData;
		buttonSubmitFormRef.current?.click();
	};

	useImperativeHandle(ref, () => ({
		openModalPopulateValuesPlanned
	}));

	return (
		<>
			<Modal 
				ref={modalPopulate}
				header={{
					title: translate("Popular valores planejados"),
					subtitle: subtitleModal,
					icon: {
						name: "settings"
					}
				}}
				footer= {{
					showSaveButton: false,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar")
				}}
				onClose={() => {
					onClose();
				}}
				onSubmit={handleSubmit(submitSpreadsheet)}
				styleInline={ModalSide}
			>
				<RCCGrid container item spacing={1}>
					<RCCGrid container item direction="column">
						<Title color="purple">
							{memorizedParams.subsidiary?.label}
						</Title>
						<Title color="purple">
							{memorizedParams.lov1?.label}
						</Title>
						<Title color="purple">
							{memorizedParams.lov2?.label}
						</Title>
						{memorizedParams.lov3 && (
							<Title color="purple">
								{memorizedParams.lov3?.label}
							</Title>
						)}
					</RCCGrid>
					<RCCGrid 
						container 
						item
						xs={12}
						sm={6}
					>
						<RCCRadio
							name="changeValues"
							register={register}
							label={translate("Alterar valores")}
							color="purple"
							options={[
								{value: "1" ,label:translate("Alterar o valor atual")},
								{value: "2" ,label:translate("Somar o valor atual")},
								{value: "3" ,label:translate("Aplicar percentual ao valor atual")},
								{value: "4" ,label:translate("Copiar ano anterior")},
								{value: "5" ,label:translate("Média ano anterior")}
							]}
							direction="vertical"
							defaultValue="1"
						/>
					</RCCGrid>
					<RCCGrid 
						container 
						item
						xs={12}
						sm={6}
					>
						<RCCRadio
							name="changeItens"
							register={register}
							label={translate("Alterar itens")}
							color="purple"
							options={[
								{value: "1" ,label: translate("Somente com valores informados")},
								{value: "2" ,label: translate("Todos os itens")}
							]}
							direction="vertical"
							defaultValue="1"
						/>
					</RCCGrid>
					<button 
						aria-label="Submit Form"
						ref={buttonSubmitFormRef}
						type="submit" 
						style={{ display: "none" }}
					></button>

					<Tabs 
						name="TabsFinancialManagement"
						tabInit={0}
						options={tabs.map((tab) => ({
							label: tab.label,
							content: tabSelected.label === tab.label ? <LoadingContainer loading={isLoading}> <Spreadsheet 
								ref={spreadsheetRef}
								data={[
									[`${formatDate(startDate, currentLanguage, false, "dd/")}`, ""],
									[`${formatDate(addMonths(startDate, 1), currentLanguage, false, "dd/")}`, ""],
									[`${formatDate(addMonths(startDate, 2), currentLanguage, false, "dd/")}`, ""],
									[`${formatDate(addMonths(startDate, 3), currentLanguage, false, "dd/")}`, ""],
									[`${formatDate(addMonths(startDate, 4), currentLanguage, false, "dd/")}`, ""],
									[`${formatDate(addMonths(startDate, 5), currentLanguage, false, "dd/")}`, ""],
									[`${formatDate(addMonths(startDate, 6), currentLanguage, false, "dd/")}`, ""],
									[`${formatDate(addMonths(startDate, 7), currentLanguage, false, "dd/")}`, ""],
									[`${formatDate(addMonths(startDate, 8), currentLanguage, false, "dd/")}`, ""],
									[`${formatDate(addMonths(startDate, 9), currentLanguage, false, "dd/")}`, ""],
									[`${formatDate(addMonths(startDate, 10), currentLanguage, false, "dd/")}`, ""],
									[`${formatDate(addMonths(startDate, 11), currentLanguage, false, "dd/")}`, ""],
									[translate("Total"), "=ROUND(SUM(B1:B12), 2)"],
									[translate("Média"), "=ROUND(AVERAGE(B1:B12), 2)"]
								]}
								editable
								columns={[
									{ type: "text", title: translate("Mês"), width: 30, readOnly: true, wordWrap: true },
									{ type: "numeric", title: translate("Planejado"), width: 130,  decimal: "," }
								]}
								readonlyLastRows={2}
								format={{
									columns: [1],
									functionToFormat: (number: string | number) => {
										if(number === "")
											return "";
										return formatNumber(number, currentLanguage, 2);
									}
								}}
								actionSaveButton={onSaveSpreadsheet}
							/> </LoadingContainer> : null,
							onClick: () => {
								setTabSelected(tab);
								// buttonSubmitFormRef.current?.click();
							}
						}))}
						styleInline="width: 100%;"
					/>
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(ModalPopulateValuesPlanned);
